import * as moment from 'moment';

import { TimeUnit as timeUnit, DayOfWeek, TimeUnit } from '../../../models';
import { IAlignmentStrategy } from './IAlignmentStrategy';
import { DatesHelper } from '../../datesHelper';

export interface IDateAlignmentHelper {
    computeNext(input: moment.Moment, beginTime: moment.Duration, interval: number, intervalTimeUnit: timeUnit, dayOfWeek: DayOfWeek, appTimeZone: string): moment.Moment;
    computePrevious(input: moment.Moment, beginTime: moment.Duration, interval: number, intervalTimeUnit: timeUnit, dayOfWeek: DayOfWeek, appTimeZone: string, isEvenIfAligned?: boolean): moment.Moment;
    getBeginDayLocalOffset(date: moment.Moment, beginDay: moment.Duration, appTimeZone: string): moment.Duration;
    registerStrategy(strategy: IAlignmentStrategy): void;
}

export const dateAlignmentHelperId = 'dateAlignmentHelper';

export class DateAlignmentHelper implements IDateAlignmentHelper {
    private _strategies: IAlignmentStrategy[];

    constructor() {
        this._strategies = [];
    }

    /**
     * Return next aligned date if input isn't aligned
     * @param input date to align
     * @param beginTime begin time of the application
     * @param interval Frequency interval
     * @param intervalTimeUnit Frequency unit
     * @param dayOfWeek Only for week alignment, first day of week defined in the application
     */
    computeNext(
        input: moment.Moment,
        beginTime: moment.Duration,
        interval: number,
        intervalTimeUnit: timeUnit,
        dayOfWeek: DayOfWeek,
        appTimeZone: string
    ): moment.Moment {
        let result = null;
        beginTime = this.getBeginDayLocalOffset(input, beginTime, appTimeZone);

        this._strategies.forEach(s => {
            if (!result) {
                result = s.align(input, beginTime, interval, intervalTimeUnit, dayOfWeek);
            }
        });
        
        if (!result) {
            result = input.clone();
        }
        return result;
    }

    /**
     * Return previous aligned date EVEN if input is aligned
     * @param input date to align
     * @param beginTime begin time of the application
     * @param interval Frequency interval
     * @param intervalTimeUnit Frequency unit
     * @param dayOfWeek Only for week alignment, first day of week defined in the application
     */
    computePrevious(
        input: moment.Moment,
        beginTime: moment.Duration,
        interval: number,
        intervalTimeUnit: timeUnit,
        dayOfWeek: DayOfWeek,
        appTimeZone: string,
        doPreviousEvenIfAligned: boolean = true
    ): moment.Moment {
        const next = this.computeNext(input, beginTime, interval, intervalTimeUnit, dayOfWeek, appTimeZone);
        const period = DatesHelper.timeUnitToStartOf(intervalTimeUnit);
        const offsetBefore = next.utcOffset();
        let previous = next.clone();
        if (doPreviousEvenIfAligned || !input.isSame(next)) {
            previous = previous.subtract(interval, period);
        }
        const offsetAfter = previous.utcOffset();
        if (offsetBefore !== offsetAfter && intervalTimeUnit < TimeUnit.Day) {
            previous = previous
                .subtract(offsetBefore, 'minutes')
                .add(offsetAfter, 'minutes');
        }
        return previous;
    }

    registerStrategy(strategy: IAlignmentStrategy) {
        this._strategies.push(strategy);
    }

    getBeginDayLocalOffset(input: moment.Moment, beginDay: moment.Duration, appTimeZone: string): moment.Duration {
        const work = input.clone();
        const localOffset = work.utcOffset();
        const appOffset = work.tz(appTimeZone).utcOffset();

        const utcBeginDay = beginDay.clone().add(-appOffset, 'minutes');
        utcBeginDay.add(localOffset, 'minutes');

        return utcBeginDay;
    }
}

export const dateAlignmentHelperInjector = [() => new DateAlignmentHelper()];